import { SearchPageType } from '../SearchContext';
import {
    getPriceFrequencyParam,
    getPriceParams,
    getBedsParams
} from './searchInitiation';

type EditSavedSearchFields = {
    userAlertId: string | null;
    description?: string | null;
    q: string;
    section: SearchPageType;
    category: 'residential';
    priceFrequency: 'per_month' | 'per_week';
    includeSharedAccommodation: string | null;
    keywords: string | null;
    identifier: string;
    radius: string | null;

    added: string | null;
    availableFrom?: string | null;

    priceMin: string | null;
    priceMax: string | null;

    bedsMin: string | null;
    bedsMax: string | null;

    rentalTerm?: 'short' | null;
    petsAllowed?: 'true' | null;
    billsIncluded?: 'true' | null;
    includeRented?: string | null;
    furnishedState?: string | null;
    propertyType?: string | null;
    propertySubType?: string | string[] | null;
    feature?: string[] | null;
    newHomes: IncludeOrExclude | 'only';
    includeSharedOwnership?: TrueOrFalse;
    isSharedOwnership?: TrueOrFalse | null;
    includeRetirementHomes?: TrueOrFalse;
    isRetirementHome?: TrueOrFalse | null;
    isAuction?: 'false' | null;
    buyerIncentive?: 'help_to_buy' | null;
    chainFree?: 'true' | null;
    reducedPriceOnly?: 'true' | null;
    includeSold?: 'true' | null;
    viewType: 'list';
    polyenc: string | null;
};

type IncludeOrExclude = 'include' | 'exclude';
type TrueOrFalse = 'true' | 'false';

type Args = {
    userAlertId: string | null;
    searchType: SearchPageType;
    // eslint-disable-next-line
    filters: any;
    createNew?: boolean;
    polyenc: string | null;
};

export const constructEditSavedSearchFields = ({
    userAlertId,
    searchType,
    filters,
    createNew = false,
    polyenc
}: Args) => {
    const {
        location,
        propertyType,
        propertySubType,
        priceFrequency,
        includeShared,
        keywords,
        radius,
        propertyAdded,
        availableFrom,
        price,
        priceWeekly,
        priceMonthly,
        beds,
        features,
        includeLet,
        furnishings,
        chainFree,
        underOfferOrSold,
        priceReduced,
        newHomes,
        isSharedOwnership,
        auction,
        retirementHome,
        helpToBuy,
        shortLets,
        billsIncluded,
        petsAllowed
    } = filters;

    const prices = getPriceParams(price, priceMonthly, priceWeekly);
    const bedsParams = getBedsParams(beds);
    const isToRent = searchType === 'to-rent';
    const isPropertyTypeFlatsOrHouses =
        propertyType === 'houses' || propertyType === 'flats';
    const includeIsAuction =
        (isPropertyTypeFlatsOrHouses && auction !== '') ||
        (!isPropertyTypeFlatsOrHouses && auction === 'false');
    const editFields: Partial<EditSavedSearchFields> = {
        userAlertId: createNew ? '' : userAlertId,
        description: createNew ? (location.value as string) : null,
        section: searchType,
        category: 'residential',
        q: location.value as string,
        identifier: location.id,
        priceFrequency:
            getPriceFrequencyParam(searchType, priceFrequency) || 'per_month',
        includeSharedAccommodation: includeShared ? 'true' : '',
        keywords: keywords ? keywords : '',
        radius: radius ? radius : '0',

        added: propertyAdded ? propertyAdded : '',
        availableFrom: isToRent ? availableFrom || '' : null,

        priceMin: prices.price_min,
        priceMax: prices.price_max,

        bedsMin: bedsParams.beds_min || '',
        bedsMax: bedsParams.beds_max || '',

        includeRented: isToRent ? (includeLet ? 'true' : null) : null,
        propertyType: propertyType || null,
        propertySubType: propertySubType.length > 0 ? propertySubType : null,

        chainFree: chainFree ? 'true' : null,
        reducedPriceOnly: priceReduced ? 'true' : null,
        includeSold: underOfferOrSold ? 'true' : null,

        rentalTerm: shortLets ? 'short' : null,
        billsIncluded: billsIncluded ? billsIncluded : null,
        petsAllowed: petsAllowed ? petsAllowed : null,

        furnishedState:
            furnishings && furnishings !== 'Any' ? furnishings : null,

        newHomes: newHomes === '' ? 'include' : newHomes,
        includeSharedOwnership: isSharedOwnership === 'true' ? 'true' : 'false',
        isSharedOwnership: isSharedOwnership === 'true' ? null : 'false',
        isAuction: includeIsAuction
            ? auction === 'true'
                ? null
                : 'false'
            : null,
        includeRetirementHomes: retirementHome !== 'false' ? 'true' : 'false',
        isRetirementHome:
            (retirementHome && isPropertyTypeFlatsOrHouses) ||
            retirementHome === 'false'
                ? retirementHome
                : null,
        buyerIncentive: helpToBuy ? 'help_to_buy' : null,

        feature: features?.length > 0 ? features : null,

        viewType: 'list',
        polyenc
    };

    type EditKeyFields = keyof Partial<EditSavedSearchFields>;

    Object.keys(editFields).forEach((k) => {
        if (editFields[k as EditKeyFields] === null) {
            delete editFields[k as EditKeyFields];
        }
    });

    return editFields;
};
